/**
 * Data Usage Tracker
 *
 * In-memory accumulator for client-side network data usage, split by user
 * activity. Used to understand where the app spends a user's bandwidth and to
 * surface avoidable/wasted bytes. See .claude/rules/data-usage-monitoring.md.
 *
 * Recording is **always on** — the accumulator is a handful of integers in
 * memory and reads bytes that are already in hand (no extra fetches, no
 * response cloning). The settings toggle in Edit Profile → Developer Tools
 * ("Show Data Monitor") controls UI/console visibility only: the floating
 * chip, the open panel, and the periodic console summaries. Recording stays
 * on regardless so the moment an admin opens the panel they see real numbers.
 *
 * Inspect from the console: window.__dataUsage.snapshot() / .log() / .reset()
 *
 * The dominant path (MSE clip playback) is counted manually where we already
 * read the response body (exact, no CORS issue). A passive PerformanceObserver
 * mops up the rest (app bundle, control plane). Cross-origin R2 assets read
 * as 0 from the observer (no Timing-Allow-Origin) — thumbnails close this gap
 * via Decision 2B (DB-persisted size, attributed in BatchPresignedUrlContext).
 * Any remaining cross-origin asset is tracked as a request count only.
 */

export type UsageCategory =
  | 'clip'        // MSE video/photo playback segments (the big one)
  | 'thumbnail'   // timeline thumbnails / optimized photos
  | 'full_video'  // crop editor full-file downloads
  | 'music'       // background music tracks
  | 'upload'      // original media sent to R2 (upstream)
  | 'control'     // edge-function JSON (presigned URLs, metadata)
  | 'app_asset'   // JS/CSS/fonts/icons
  | 'other';

const CATEGORIES: UsageCategory[] = [
  'clip', 'thumbnail', 'full_video', 'music', 'upload', 'control', 'app_asset', 'other',
];

interface CategoryStats {
  wireBytes: number;     // best-known transferred bytes (0 for cross-origin w/o TAO)
  logicalBytes: number;  // would-be size ignoring cache (when known)
  requests: number;
}

interface WasteStats {
  repairBytes: number;      // clip bytes paid twice (404 -> regen -> re-fetch)
  reFetchBytes: number;     // same clip fetched again (seek/eviction) or upload retries
  duplicateUrlCount: number;
}

export interface DataUsageSnapshot {
  enabled: boolean;
  startedAt: number;
  elapsedMs: number;
  byCategory: Record<UsageCategory, CategoryStats>;
  totalWireBytes: number;
  waste: WasteStats;
  prefetchWasteBytes: number;   // bytes for clips fetched but never played (computed live)
  clipsFetched: number;
  clipsPlayed: number;
  connection: { effectiveType?: string; saveData?: boolean; downlink?: number } | null;
}

interface RecordOpts {
  logicalBytes?: number;
  url?: string;          // exact request URL — lets the observer skip what we counted
  key?: string;          // stable identity for re-fetch detection (e.g. videoId)
  projectId?: string;
  repair?: boolean;      // this was a repair re-download
  cacheHit?: boolean;
  /**
   * Count this key at most once for the whole session. A repeat is silently
   * ignored — NOT flagged as re-fetch waste. Use for logical-byte attribution
   * where multiple code paths may report the same asset (e.g. a thumbnail
   * counted by both the batch context and the refresh-fallback hook), and where
   * a re-render from the same cached file is not a real second download.
   */
  once?: boolean;
}

function freshCategoryStats(): Record<UsageCategory, CategoryStats> {
  return CATEGORIES.reduce((acc, c) => {
    acc[c] = { wireBytes: 0, logicalBytes: 0, requests: 0 };
    return acc;
  }, {} as Record<UsageCategory, CategoryStats>);
}

// ---- module state -----------------------------------------------------------

let startedAt = Date.now();
let byCategory = freshCategoryStats();
const waste: WasteStats = { repairBytes: 0, reFetchBytes: 0, duplicateUrlCount: 0 };
const seenKeys = new Set<string>();          // for re-fetch detection
const fetchedClipBytes = new Map<string, number>(); // videoId -> bytes (prefetch waste)
const playedClips = new Set<string>();
const manualUrls = new Set<string>();        // URLs counted manually -> observer skips
const listeners = new Set<(s: DataUsageSnapshot) => void>();

/**
 * Recording is always on. The accumulator is a few small integers in memory
 * and reads from response bytes we already hold — there is no admin scenario
 * where it's useful to turn it off, and turning it off creates blind periods
 * that are worse than the negligible cost of always recording. The settings
 * toggle (isDataUsageMonitorVisible) controls UI/console visibility only.
 *
 * Kept as a function (not a constant) so we don't ripple a breaking change
 * through every call site, and so we have one place to gate this later if
 * the cost ever changes.
 */
export function isDataUsageEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return true;
}

/**
 * Whether the data-usage debug overlay (floating pill / open panel) and
 * console summaries (boot banner, every-30s table, pagehide flush) should
 * be visible. Recording itself is unconditional — this only controls
 * UI/console noise. Opt-in via the settings menu
 * (writes localStorage.DEBUG_DATA_USAGE_CONSOLE).
 *
 * Storage key kept for backwards compat with anyone who already flipped
 * it on while the flag existed.
 */
export function isDataUsageMonitorVisible(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('DEBUG_DATA_USAGE_CONSOLE') === 'true';
}

// Old name preserved as an alias for any in-tree callers.
export const isDataUsageConsoleLogEnabled = isDataUsageMonitorVisible;

function getConnection(): DataUsageSnapshot['connection'] {
  if (typeof navigator === 'undefined') return null;
  // navigator.connection is not in the TS lib DOM types everywhere
  const c = (navigator as unknown as { connection?: { effectiveType?: string; saveData?: boolean; downlink?: number } }).connection;
  if (!c) return null;
  return { effectiveType: c.effectiveType, saveData: c.saveData, downlink: c.downlink };
}

function computePrefetchWaste(): number {
  let total = 0;
  for (const [videoId, bytes] of fetchedClipBytes) {
    if (!playedClips.has(videoId)) total += bytes;
  }
  return total;
}

export function getDataUsageSnapshot(): DataUsageSnapshot {
  const totalWireBytes = CATEGORIES.reduce((sum, c) => sum + byCategory[c].wireBytes, 0);
  return {
    enabled: isDataUsageEnabled(),
    startedAt,
    elapsedMs: Date.now() - startedAt,
    byCategory: JSON.parse(JSON.stringify(byCategory)),
    totalWireBytes,
    waste: { ...waste },
    prefetchWasteBytes: computePrefetchWaste(),
    clipsFetched: fetchedClipBytes.size,
    clipsPlayed: playedClips.size,
    connection: getConnection(),
  };
}

function notify() {
  if (listeners.size === 0) return;
  const snap = getDataUsageSnapshot();
  listeners.forEach(fn => { try { fn(snap); } catch { /* listener error must not break recording */ } });
}

export const dataUsageTracker = {
  /**
   * Record bytes transferred for a category. Cheap; no-op when disabled.
   */
  record(category: UsageCategory, wireBytes: number, opts: RecordOpts = {}): void {
    if (!isDataUsageEnabled()) return;

    const id = opts.key ?? opts.url;

    // `once`: count a given key a single time per session. A repeat is not a
    // real second download (same cached asset, or a second code path reporting
    // the same thing), so we skip it entirely — no bytes, no re-fetch waste.
    if (opts.once && id && seenKeys.has(id)) {
      if (opts.url) manualUrls.add(opts.url); // still let the observer skip it
      return;
    }

    const stats = byCategory[category];
    stats.wireBytes += wireBytes;
    stats.logicalBytes += opts.logicalBytes ?? wireBytes;
    stats.requests += 1;

    if (opts.url) manualUrls.add(opts.url);
    if (opts.repair) waste.repairBytes += wireBytes;

    if (id) {
      if (seenKeys.has(id)) {
        waste.reFetchBytes += wireBytes;
        waste.duplicateUrlCount += 1;
      } else {
        seenKeys.add(id);
      }
    }
    if (category === 'clip' && opts.key) {
      fetchedClipBytes.set(opts.key, (fetchedClipBytes.get(opts.key) ?? 0) + wireBytes);
    }
  },

  /** Mark a clip as actually played (it became the current clip). */
  markClipPlayed(videoId: string): void {
    if (!isDataUsageEnabled()) return;
    playedClips.add(videoId);
  },

  /** Was this exact URL already counted manually? (observer dedup) */
  wasCountedManually(url: string): boolean {
    return manualUrls.has(url);
  },

  subscribe(fn: (s: DataUsageSnapshot) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  getSnapshot: getDataUsageSnapshot,

  reset(): void {
    startedAt = Date.now();
    byCategory = freshCategoryStats();
    waste.repairBytes = 0;
    waste.reFetchBytes = 0;
    waste.duplicateUrlCount = 0;
    seenKeys.clear();
    fetchedClipBytes.clear();
    playedClips.clear();
    manualUrls.clear();
    notify();
  },

  /**
   * Print a formatted summary to the console. Gated by the console-log opt-in
   * so the periodic 30s noise stays off for everyone unless they ask for it.
   * The admin panel's "Log" button calls this directly — when invoked from a
   * user action we still want output, so the user-action path passes `force`.
   */
  logSummary(reason = 'summary', opts: { force?: boolean } = {}): void {
    if (!isDataUsageEnabled()) return;
    if (!opts.force && !isDataUsageConsoleLogEnabled()) return;
    const s = getDataUsageSnapshot();
    const fmt = (b: number) => formatBytes(b);
    const conn = s.connection ? `${s.connection.effectiveType ?? '?'}${s.connection.saveData ? ' saveData' : ''}` : 'n/a';
    console.groupCollapsed(
      `%c[DataUsage] ${fmt(s.totalWireBytes)} in ${(s.elapsedMs / 1000).toFixed(0)}s (${reason}) — ${conn}`,
      'background:#6366f1;color:white;padding:2px 6px;border-radius:3px;font-weight:bold'
    );
    console.table(
      CATEGORIES
        .filter(c => s.byCategory[c].requests > 0)
        .reduce((rows, c) => {
          rows[c] = {
            wire: fmt(s.byCategory[c].wireBytes),
            logical: fmt(s.byCategory[c].logicalBytes),
            requests: s.byCategory[c].requests,
          };
          return rows;
        }, {} as Record<string, { wire: string; logical: string; requests: number }>)
    );
    console.log(
      `Waste — repair: ${fmt(s.waste.repairBytes)}, re-fetch: ${fmt(s.waste.reFetchBytes)} ` +
      `(${s.waste.duplicateUrlCount} dupes), prefetched-unwatched: ${fmt(s.prefetchWasteBytes)} ` +
      `| clips played ${s.clipsPlayed}/${s.clipsFetched}`
    );
    console.groupEnd();
  },
};

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(val < 10 ? 2 : 1)} ${units[i]}`;
}

// ---- PerformanceObserver net + lifecycle ------------------------------------

let monitoringStarted = false;

function categorizeResource(url: string, initiatorType: string): UsageCategory | null {
  // Clips and uploads are counted manually — never double-count them here.
  if (/_cropped_mini\.mp4(\?|$)/.test(url)) return null;
  if (/\/generate-upload-url(\?|$)/.test(url)) return 'control';

  if (/_thumbnail\.jpg(\?|$)/.test(url) || /_cropped_mini\.jpg(\?|$)/.test(url)) return 'thumbnail';
  if (initiatorType === 'img') return 'thumbnail';
  if (/\/functions\/v1\//.test(url)) return 'control';
  if (initiatorType === 'audio' || /\/music\//.test(url) || /template-music\//.test(url)) return 'music';
  if (['script', 'link', 'css', 'font', 'fetch', 'xmlhttprequest'].includes(initiatorType)) {
    // Same-origin app assets have accurate sizes; cross-origin reads as 0.
    return 'app_asset';
  }
  return 'other';
}

function handleResourceEntries(entries: PerformanceEntryList): void {
  for (const entry of entries) {
    const e = entry as PerformanceResourceTiming;
    if (dataUsageTracker.wasCountedManually(e.name)) continue;
    const category = categorizeResource(e.name, e.initiatorType);
    if (!category) continue;
    const cacheHit = e.transferSize === 0 && e.decodedBodySize > 0;
    dataUsageTracker.record(category, e.transferSize, {
      logicalBytes: e.decodedBodySize || e.encodedBodySize || 0,
      url: e.name,
      cacheHit,
    });
  }
}

/**
 * Start monitoring: passive resource observer, periodic console summary
 * (gated by the visibility flag), flush-on-hide, and window.__dataUsage
 * inspection hooks. Recording is unconditional; the visibility flag only
 * controls console output and the admin panel.
 */
export function startDataUsageMonitoring(): void {
  if (monitoringStarted || typeof window === 'undefined') return;
  monitoringStarted = true;

  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver(list => handleResourceEntries(list.getEntries()));
      observer.observe({ type: 'resource', buffered: true });
      // Keep the resource buffer from filling up.
      setInterval(() => performance.clearResourceTimings?.(), 30_000);
    } catch {
      /* observer unsupported — manual counting still works */
    }
  }

  // Periodic + pagehide summaries are gated by the console-log flag so they
  // only fire when an admin explicitly opted in. The methods themselves
  // recheck the flag at call time — flipping it without a reload still works.
  setInterval(() => dataUsageTracker.logSummary('periodic'), 30_000);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') dataUsageTracker.logSummary('pagehide');
  });

  // Console inspection: window.__dataUsage.snapshot() / .log() / .reset()
  // .log() is a deliberate user action → force output regardless of flag.
  (window as unknown as { __dataUsage?: unknown }).__dataUsage = {
    snapshot: getDataUsageSnapshot,
    log: () => dataUsageTracker.logSummary('manual', { force: true }),
    reset: () => dataUsageTracker.reset(),
  };

  if (isDataUsageConsoleLogEnabled()) {
    console.log(
      '%c[DataUsage] Phase 0 monitoring active%c — window.__dataUsage.log() for a summary',
      'background:#6366f1;color:white;padding:2px 6px;border-radius:3px;font-weight:bold',
      'color:inherit'
    );
  }
}
